import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import PageHeader from '../components/PageHeader';
import { Avatar, Ic, Spinner, fmtNum } from '../components/ui';

const statusLabel = { pending: 'Bekliyor', reviewing: 'İnceleniyor', accepted: 'Kabul', rejected: 'Red' };
const statusClass = { pending: 'warning', reviewing: 'info', accepted: 'success', rejected: 'danger' };

function StatCard({ icon, label, value, color, bg, sub, to }) {
  const body = (
    <div className="stat-card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, cursor: to ? 'pointer' : 'default' }}>
      <div className="ic-circle" style={{ width: 48, height: 48, background: bg, color }}><Ic n={icon} s={24} /></div>
      <div style={{ minWidth: 0 }}>
        <div className="stat-value" style={{ marginBottom: 2 }}>{value}</div>
        <div className="stat-label">{label}</div>
        {sub && <div style={{ fontSize: 12.5, color, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>{body}</Link> : body;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ bots: 0, activeBots: 0, applications: 0, pending: 0, users: 0 });
  const [bots, setBots] = useState([]);
  const [recentApps, setRecentApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [botsRes, appsRes] = await Promise.all([api.get('/bots'), api.get('/applications')]);
        const b = botsRes.data || [];
        const apps = appsRes.data || [];
        setBots(b);
        setStats({
          bots: b.length,
          activeBots: b.filter(x => x.is_active).length,
          applications: apps.length,
          pending: apps.filter(a => a.status === 'pending').length,
          users: b.reduce((s, x) => s + parseInt(x.user_count || 0, 10), 0),
        });
        setRecentApps(apps.slice(0, 6));
      } catch (e) { /* noop */ }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh' }}><Spinner /></div>;

  const hour = new Date().getHours();
  const greet = hour < 6 ? 'İyi geceler' : hour < 12 ? 'Günaydın' : hour < 18 ? 'İyi günler' : 'İyi akşamlar';
  const username = localStorage.getItem('username') || '';

  return (
    <div className="page-enter">
      <PageHeader title={`${greet}${username ? ', ' + username : ''}`} subtitle="Sisteme genel bakış" />

      <div className="keep-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 230px), 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard icon="bot" label="Toplam bot" value={fmtNum(stats.bots)} color="var(--link)" bg="var(--accent-s)" sub={`${stats.activeBots} aktif`} to="/bots" />
        <StatCard icon="users" label="Kayıtlı kullanıcı" value={fmtNum(stats.users)} color="var(--success)" bg="var(--success-s)" />
        <StatCard icon="clipboard" label="Toplam başvuru" value={fmtNum(stats.applications)} color="var(--purple)" bg="var(--purple-s)" to="/applications" />
        <StatCard icon="clock" label="Bekleyen başvuru" value={fmtNum(stats.pending)} color="var(--warning)" bg="var(--warning-s)" to="/applications" />
      </div>

      <div className="grid-2">
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="sec-title">
            <span className="flex-center gap-8"><Ic n="clipboard" s={18} c="var(--link)" />Son başvurular</span>
            <Link to="/applications" className="btn btn-sm btn-ghost" style={{ textDecoration: 'none' }}>Tümü<Ic n="arrowRight" s={14} /></Link>
          </div>
          {recentApps.length === 0 ? (
            <div className="empty-state"><div className="icon"><Ic n="clipboard" s={40} /></div><p>Henüz başvuru yok</p></div>
          ) : (
            <div className="list">
              {recentApps.map(app => (
                <div key={app.id} className="list-row click" onClick={() => navigate('/applications')}>
                  <Avatar id={app.id} name={app.full_name || '?'} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="truncate" style={{ fontWeight: 600 }}>{app.full_name || '—'}</div>
                    <div className="truncate text-muted" style={{ fontSize: 13 }}>{app.bot_name} · {new Date(app.created_at).toLocaleDateString('tr-TR')}</div>
                  </div>
                  <span className={`badge badge-${statusClass[app.status] || 'neutral'}`}>{statusLabel[app.status] || app.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="sec-title">
            <span className="flex-center gap-8"><Ic n="bot" s={18} c="var(--link)" />Botlar</span>
            <Link to="/bots" className="btn btn-sm btn-ghost" style={{ textDecoration: 'none' }}>Yönet<Ic n="arrowRight" s={14} /></Link>
          </div>
          {bots.length === 0 ? (
            <div className="empty-state"><div className="icon"><Ic n="bot" s={40} /></div><p>Henüz bot yok</p></div>
          ) : (
            <div className="list">
              {bots.slice(0, 6).map(b => (
                <div key={b.id} className="list-row click" onClick={() => navigate('/bots')}>
                  <Avatar id={b.id} name={b.name} size={40}>{b.bot_type === 'date' ? <Ic n="heart" s={20} /> : null}</Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="truncate" style={{ fontWeight: 600 }}>{b.name}</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>{fmtNum(b.user_count)} kullanıcı</div>
                  </div>
                  <span className={'badge ' + (b.is_active ? 'badge-success' : 'badge-neutral')}><span className={'dot ' + (b.is_active ? 'dot-green' : 'dot-gray')} />{b.is_active ? 'Aktif' : 'Pasif'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
